import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/apiBase";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import DatePicker from "react-datepicker";
import { FaCalendarAlt, FaCheck, FaHourglassHalf, FaTimes } from "react-icons/fa";
import "react-datepicker/dist/react-datepicker.css";
import "./Transactions.css";

const ITEMS_PER_PAGE = 10;

const SessionScheduleTracker = () => {
  const [progressData, setProgressData] = useState([]);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filteredData, setFilteredData] = useState([]);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [progressRes, usersRes, coursesRes, schedulesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/progress/allProgress`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/users`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/courses/allCourses`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/schedules/all`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setProgressData(progressRes.data);
        setFilteredData(progressRes.data); // Initialize
        setUsers(usersRes.data);
        setCourses(coursesRes.data);
        setSchedules(schedulesRes.data);
      } catch (err) {
        console.error("Error loading data:", err);
      }
    };

    fetchData();
  }, []);

  const getUserName = (id) => {
    const user = users.find((u) => u.id === id);
    return user ? user.name : "—";
  };

  const getCourseTitle = (id) => {
    const course = courses.find((c) => c.id === id);
    return course ? course.title : "—";
  };

  const getFirstClassDate = (userId, courseId) => {
    const userSchedules = schedules.filter(
      (s) => s.course?.id === courseId && s.users?.some((u) => u && u.id === userId)
    );
    if (!userSchedules.length) return null;
    const earliest = userSchedules.reduce((a, b) =>
      new Date(a.date) < new Date(b.date) ? a : b
    );
    return new Date(earliest.date);
  };

  const getSessionStatus = (date) => {
    if (!date) return { label: "Not Scheduled", icon: <FaTimes />, className: "status-pending" };
    const today = new Date().toISOString().slice(0, 10);
    const sessionDate = date.toISOString().slice(0, 10);
    if (sessionDate < today)
      return { label: "Completed", icon: <FaCheck />, className: "status-completed" };
    if (sessionDate >= today)
      return { label: "Upcoming", icon: <FaHourglassHalf />, className: "status-onhold" };
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    const filtered = progressData.filter((record) => {
      const classDate = getFirstClassDate(record.userId, record.courseId);
      return (
        (!start || !classDate || classDate >= start) &&
        (!end || !classDate || classDate <= end)
      );
    });

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className="dashboard-wrapper">
      <div className="sidebar-fixed">
        <Sidebar />
      </div>
      <div className="dashboard-main">
        <Navbar />

        <div className="transaction-content">
          <div className="table-container">
            <div className="table-header">
              <h4 className="section-title">📅 Session Schedule Tracker</h4>
              <div className="date-range">
                <FaCalendarAlt />
                <DatePicker
                  selected={startDate}
                  onChange={handleDateRangeChange}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange
                  isClearable
                  placeholderText="Filter by date range"
                  className="date-picker-input"
                />
              </div>
            </div>

            <div className="table-scroll-wrapper">
              <table className="transactions-table">
                <thead>
                  <tr>
                    <th>Student Name</th>
                    <th>Course</th>
                    <th>Coach</th>
                    <th>Scheduled Date</th>
                    <th>Session Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((record) => {
                    const classDate = getFirstClassDate(record.userId, record.courseId);
                    const sessionStatus = getSessionStatus(classDate);
                    return (
                      <tr key={record.id}>
                        <td>{getUserName(record.userId)}</td>
                        <td>{getCourseTitle(record.courseId)}</td>
                        <td>{record.coach || "—"}</td>
                        <td>{classDate ? classDate.toISOString().slice(0, 10) : "—"}</td>
                        <td className={sessionStatus.className}>
                          {sessionStatus.icon} {sessionStatus.label}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="pagination">
                {Array.from({
                  length: Math.ceil(filteredData.length / ITEMS_PER_PAGE),
                }).map((_, index) => (
                  <button
                    key={index}
                    className={currentPage === index + 1 ? "active" : ""}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionScheduleTracker;
